import { SummaryResultNetworkType, SummaryResultScope, SummaryResultType } from "@app/shared/data-summaries";
import { isEqual } from "lodash";
import { Component } from "react";
import { ExportFormatType, ReportSettingsType } from "../enumerations";
import { ExportSummariesDetailProvidersRequestModel, ReportSettingsModel } from "../exportModels";
import ExportService from "../exportService";
import * as helpers from "./exportPanelBodyHelpers";
import { SelectFormat } from "./filters";
import GenerateBody from "./generate-body";
import { ReportSettingsBody, ReportSettingsSaveAction } from "./report-settings";
import { ReportSettingsProps, ReportSettingsState } from "./report-settings/reportSettings";
import { ReportPanelBodyBaseState } from "./reportPanelBodyBase";
import * as toast from "./toast";
import { ReportPanelBodyBaseProps } from "./reportPanelBodyBaseProps";

interface SummariesDetailPanelBodyProps extends ReportPanelBodyBaseProps, ReportSettingsProps {
  networkType: SummaryResultNetworkType;
  summaryKey: string;
  displayKey: string;
  scope: SummaryResultScope;
  resultType: SummaryResultType;
  defaultReportName: string;
  exportSummaryProviderCount: number;
  excelRecordsLimit: number;
}

interface SummariesDetailPanelBodyState extends ReportPanelBodyBaseState, ReportSettingsState {
  sharedUsers: number[];
}

export default class SummariesDetailPanelBody extends Component<SummariesDetailPanelBodyProps, SummariesDetailPanelBodyState> {
  private readonly exportService = new ExportService();

  constructor(props: SummariesDetailPanelBodyProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState(),
      ...this.getInitialReportSettingsState()
    };
  }

  public componentDidUpdate(prevProps: SummariesDetailPanelBodyProps) {
    if (!isEqual(prevProps.reportSettingsList, this.props.reportSettingsList)) {
      const reportSettingsList = this.props.reportSettingsList;
      if (reportSettingsList) {
        const currentReportSettings = reportSettingsList.find(rs => rs.isDefault);
        if (currentReportSettings)
          this.handleParseReportSettingsJsonToState(currentReportSettings.reportSettingsJson);
        this.setState({
          currentReportSettings,
          reportSettingsList
        })
      }
    }
  }

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {
    const isAccuracyModule = this.props.match.params.module === "accuracy";

    return (
      <>
        {isAccuracyModule &&
          <ReportSettingsBody
            buildReportSettingsModel={this.buildReportSettingsModel}
            createReportSettingsNameInputDisabled={this.state.createReportSettingsNameInputDisabled}
            currentReportSettings={this.state.currentReportSettings}
            isSelectionValid={this.state.isSelectionValid}
            parseReportSettingsJsonToState={this.handleParseReportSettingsJsonToState}
            reportSettingsHasChanges={this.state.reportSettingsHasChanges}
            reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
            reportSettingsList={this.state.reportSettingsList}
            saveClicked={this.state.saveClicked}
            setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
            setCurrentReportSettings={this.setCurrentReportSettings}
            setReportSettingsHasChanges={this.setReportSettingsHasChanges}
            setReportSettingsHasInvalidChanges={this.setReportSettingsHasInvalidChanges}
            setReportSettingsIsSelectionValid={this.setReportSettingsIsSelectionValid}
            setReportSettingsList={this.setReportSettingsList}
            setSaveClicked={this.setSaveClicked}
          />
        }
        <div className="row">
          <div className="col-12 col-sm-3">
            <SelectFormat
              excelDisabled={this.props.exportSummaryProviderCount > this.props.excelRecordsLimit}
              exportType="summaries-detail"
              outputFormat={this.state.outputFormat}
              onOutputFormatChange={this.handleOutputFormatChange} />
            {this.props.exportSummaryProviderCount > this.props.excelRecordsLimit && <p className="text-danger"><i>Results are too large for Microsoft Excel Workbook (.xlsx).</i></p>}
          </div>
        </div>
        {isAccuracyModule &&
          <ReportSettingsSaveAction
            currentReportSettings={this.state.currentReportSettings}
            isSelectionValid={this.state.isSelectionValid}
            reportSettingsHasChanges={this.state.reportSettingsHasChanges}
            reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
            reportSettingsList={this.state.reportSettingsList}
            saveClicked={this.state.saveClicked}
            setCurrentReportSettings={this.setCurrentReportSettings}
            setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
            setReportSettingsHasChanges={this.setReportSettingsHasChanges}
            setReportSettingsList={this.setReportSettingsList}
            setSaveClicked={this.setSaveClicked}
            stringifyStateReportSettings={this.stringifyStateReportSettings}
          />
        }
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={this.state.loadingReportSettingsJSONStrings && this.state.loadingReportSettingsJSONStrings.indexOf(helpers.stringifyStateReportSettings(this.state)) > -1}
          isValid={true}
          showSharedUsers={true}
          sharedUsers={this.state.sharedUsers}
          updateSelectedSharedUsers={this.updateSharedUsers}
          exportType="SummariesDetail"
          projectId={this.props.projectId}
        />
      </>
    );
  }

  private getInitialBodyState = (): SummariesDetailPanelBodyState => {
    return {
      emailOnSuccess: true,
      loadingReportSettingsJSONStrings: [],
      outputFormat: this.props.exportSummaryProviderCount > this.props.excelRecordsLimit ? ExportFormatType.CSV : ExportFormatType.Excel,
      reportName: this.props.defaultReportName,
      sharedUsers: [],
    } as SummariesDetailPanelBodyState;
  }

  private getInitialReportSettingsState = (): ReportSettingsState => {
    return {
      createReportSettingsNameInputDisabled: true,
      currentReportSettings: undefined,
      isSelectionValid: true,
      reportSettingsHasChanges: false,
      reportSettingsHasInvalidChanges: false,
      reportSettingsList: [],
      saveClicked: false,
    } as ReportSettingsState;
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat, reportSettingsHasChanges: true });
  }

  private handleGenerateClick = async () => {
    const stateReportSettingsJSON = helpers.stringifyStateReportSettings(this.state);
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [
        ...prevState.loadingReportSettingsJSONStrings,
        stateReportSettingsJSON,
      ],
    }));

    const summariesDetailModel: ExportSummariesDetailProvidersRequestModel = {
      customProjectId: this.props.projectId,
      displayKey: this.props.displayKey,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      key: this.props.summaryKey,
      networkType: this.props.networkType,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      resultType: this.props.resultType,
      runAsynchronous: true,
      scope: this.props.scope,
      sharedUsers: this.state.sharedUsers,
    };

    try {
      await this.exportService.postSummariesDetailProviders(summariesDetailModel);
      toast.toastInfo(this.state.reportName);
    } catch {
      toast.toastError(this.state.reportName);
    }
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess, reportSettingsHasChanges: true });
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  private updateSharedUsers = (sharedUsers: number[]) => {
    this.setState({ sharedUsers });
  }

  // Report Settings
  private stringifyStateReportSettings = (): string => {
    return helpers.stringifyStateReportSettings({
      emailOnSuccess: this.state.emailOnSuccess,
      loadingReportSettingsJSONStrings: this.state.loadingReportSettingsJSONStrings,
      outputFormat: this.state.outputFormat,
    } as ReportPanelBodyBaseState);
  }

  private buildReportSettingsModel = (isDefault: boolean, name: string): ReportSettingsModel => {
    const reportSettingsJson = this.stringifyStateReportSettings();
    return {
      isDefault,
      name,
      reportSettingsJson,
      type: this.reportSettingsType,
    } as ReportSettingsModel;
  }

  private get reportSettingsType() {
    switch (this.props.match.params.module) {
      case "accuracy":
        return ReportSettingsType.AccuracySummariesDetail;
      default:
        return ReportSettingsType.Unknown;
    }
  }

  private handleParseReportSettingsJsonToState = (reportSettingsJson: string) => {
    const settings = helpers.tryParseReportSettings(reportSettingsJson);
    if (settings) {
      this.setReportSettingsIsSelectionValid(true);
      this.setState((prevState) => ({
        ...prevState,
        ...settings,
      }));
    }
    else {
      const initialBodyState = this.getInitialBodyState();
      this.setReportSettingsIsSelectionValid(false);
      this.setState((prevState) => ({
        ...prevState,
        ...initialBodyState
      }));
    }
  }

  private setCurrentReportSettings = (currentReportSettings: ReportSettingsModel) => {
    this.setState((prevState) => ({
      ...prevState,
      currentReportSettings,
    }));
  }

  private setReportSettingsList = (rsList: ReportSettingsModel[]) => {
    const reportSettingsList = this.props.sortedReportSettings(rsList, "name");
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsList,
    }))
  }

  private setReportSettingsHasChanges = (reportSettingsHasChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges,
    }))
  }

  private setReportSettingsHasInvalidChanges = (reportSettingsHasInvalidChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasInvalidChanges,
    }))
  }

  private setReportSettingsIsSelectionValid = (isSelectionValid: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      isSelectionValid,
    }))
  }

  private setCreateReportSettingsNameInputDisabled = (createReportSettingsNameInputDisabled: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      createReportSettingsNameInputDisabled,
    }))
  }

  private setSaveClicked = (saveClicked: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      saveClicked,
    }))
  }
}
